import React, { forwardRef } from 'react';

import type { FieldRenderProps } from 'react-final-form';
import styled from '@emotion/styled';

import Box from '../../box/box';
import Label from '../label/label';

type StyledRadioButtonProps = {
  /** Checks if the field has an error message. */
  hasError?: boolean;
  /** Checks if the checkbox field has instructions text. */
  hasInstructions?: boolean;
};

export interface RadioButtonProps extends FieldRenderProps<string, HTMLInputElement> {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The instructions for the checkbox. */
  instructions?: string;
  /** Checks if the field is disabled. */
  isDisabled?: boolean;
  /** Checks if the field is hidden. */
  isHidden?: boolean;
  /** Checks if the field is required to submit. */
  isRequired?: boolean;
  /** The unique id for the component */
  id: string;
  /** The label for the radio button */
  label: string;
  /** The type of the input */
  type?: 'radio';
  /** Checks if the radio button is checked */
  checked?: boolean;
}

const StyledRadioButton = styled.input<StyledRadioButtonProps>(({ disabled, theme, required }) => [
  {
    label: 'radioButton',
    outlineOffset: 0,
    transition: 'background-color 0.2s ease-out',
    '&:focus': {
      border: `2px solid ${theme.colors.coolGrey[100]}`,
    },
    '&:checked': {
      outline: 'none',
    },
  },
  disabled && {
    pointerEvents: 'none',
  },
  required && {
    '&:focus': {
      color: theme.colors.pink[80],
    },
  },
]);

const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
  (
    { id, input, isDisabled = false, isRequired = false, label, meta, type = 'radio', checked, ...rest },
    forwardedRef,
  ) => {
    const hasError = meta?.error && meta?.touched;
    const isChecked = checked;

    return (
      <Box
        aria-checked={isChecked}
        as="span"
        css={{
          label: 'radioButtonContainer',
          paddingBottom: '10px',
        }}
        role="radio"
        tabIndex={isChecked ? 0 : -1}
      >
        <StyledRadioButton
          {...input}
          {...rest}
          ref={forwardedRef}
          aria-describedby={hasError ? `fieldErrorMessage-${id}` : undefined}
          aria-invalid={hasError}
          css={theme => ({
            order: 1,
            marginRight: theme.space.s24,
          })}
          data-testid={`formRadioButton-${id}`}
          disabled={isDisabled}
          hasError={hasError}
          id={id}
          required={isRequired}
          type={type}
        />
        <Label htmlFor={id} isDisabled={isDisabled} label={label} />
      </Box>
    );
  },
);

export { RadioButton };
export default RadioButton;
